import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom'
import { AuthKitProvider, useAuth } from '@workos-inc/authkit-react'
import { AccountBlockedProvider } from './auth/context/AccountBlockedContext'
import { SessionExpiryProvider } from './auth/context/SessionExpiryContext'
import { setAccessTokenGetter } from './auth/services/api-client'
import { getMe } from './auth/services/auth-api'
import { isAccountBlocked, getBlockedMessage } from './auth/hooks/useMe'
import { captureSessionId, debugDumpAuthState } from './auth/utils/session'
import { AuthApp } from './auth/AuthApp'
import { EngineerRoutes } from './components/layout/EngineerRoutes'
import { ProtectedRoute } from './auth/components/ProtectedRoute'
import { TenantSlugLayout } from './auth/components/TenantSlugLayout'
import { OnboardingDashboard } from './auth/pages/onboarding/OnboardingDashboard'
// import { OnboardingUsers } from './auth/pages/onboarding/OnboardingUsers'
import { Orders } from './pages/Orders'
import { OrderDetail } from './pages/OrderDetail'
import { SupplierChangeOrder } from './pages/SupplierChangeOrder'
import { OnboardingProfile } from './auth/pages/onboarding/OnboardingProfile'
import { OnboardingSettings } from './auth/pages/onboarding/OnboardingSettings'
import { SpendCapsSettings } from './auth/pages/onboarding/SpendCapsSettings'
import { NotFoundPage } from './auth/components/NotFoundPage'
import { NoAccessPage } from './auth/pages/NoAccessPage'
import { TenantMismatchPage } from './auth/pages/TenantMismatchPage'
import { RequirePermission } from './auth/components/RequirePermission'
import { usePermission } from './hooks/usePermission'
import { useMe } from './auth/hooks/useMe'
import { LoadingSpinner } from './auth/components/LoadingSpinner'
import { FeaturebaseSsoPage } from './pages/FeaturebaseSsoPage'
import { FEATUREBASE_SSO_PATH } from './lib/featurebase-sso'

const WORKOS_CLIENT_ID = import.meta.env.VITE_WORKOS_CLIENT_ID || ''

/**
 * Landing page for `/:tenantSlug/`. Picks the first admin route the user
 * has permission for. Prevents settings-only users from being bounced by
 * a hard-coded redirect to `dashboard` when they don't hold view_dashboard.
 */
function AdminIndexRedirect() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const { can } = usePermission()
  if (can('view_dashboard')) return <Navigate to={`/${tenantSlug}/dashboard`} replace />
  if (can('view_all_orders') || can('view_approvals')) {
    return <Navigate to={`/${tenantSlug}/orders`} replace />
  }
  if (can('view_settings')) return <Navigate to={`/${tenantSlug}/settings`} replace />
  return <Navigate to={`/${tenantSlug}/no-access`} replace />
}

/**
 * Guards the entire admin side (TenantSlugLayout + all child routes).
 *
 * Any admin-side capability grants entry — view_dashboard, view_settings,
 * view_all_orders, view_approvals, or manage_users. Client feedback
 * 2026-07-16 asked that Settings be permission-gated, not role-gated: a
 * user with `settings.view` should be able to reach the Settings page even
 * if they don't have `dashboard.view`. Individual child routes still guard
 * themselves via `RequirePermission`, so relaxing this gate can't leak
 * pages that need a stricter permission.
 *
 * Users with none of these fall through to engineer (if they have
 * search_products) or no-access.
 */
function AdminLayoutGuard() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const { canAny, can } = usePermission()
  const { data: me, isLoading } = useMe()

  if (isLoading || !me) return <LoadingSpinner message="Loading dashboard..." />

  // Block access if URL slug doesn't match the user's actual tenant
  const correctSlug = me.tenant?.slug
  if (correctSlug && tenantSlug !== correctSlug) {
    return (
      <TenantMismatchPage
        userEmail={me.email}
        tenantName={me.tenant?.name ?? ''}
        correctSlug={correctSlug}
      />
    )
  }

  const hasAdminAccess = canAny(
    'view_dashboard',
    'view_settings',
    'view_all_orders',
    'view_approvals',
    'manage_users',
  )
  if (hasAdminAccess) return <TenantSlugLayout />
  if (can('search_products')) return <Navigate to={`/${tenantSlug}/engineer`} replace />
  return <Navigate to={`/${tenantSlug}/no-access`} replace />
}

/**
 * Guards the entire engineer side.
 * Users without search_products are redirected to dashboard or no-access.
 */
function EngineerLayoutGuard() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const { can } = usePermission()
  const { data: me, isLoading } = useMe()

  if (isLoading || !me) return <LoadingSpinner message="Loading engineer..." />

  // Block access if URL slug doesn't match the user's actual tenant
  const correctSlug = me.tenant?.slug
  if (correctSlug && tenantSlug !== correctSlug) {
    return (
      <TenantMismatchPage
        userEmail={me.email}
        tenantName={me.tenant?.name ?? ''}
        correctSlug={correctSlug}
      />
    )
  }

  if (can('search_products')) return <EngineerRoutes />
  if (can('view_dashboard')) return <Navigate to={`/${tenantSlug}/dashboard`} replace />
  return <Navigate to={`/${tenantSlug}/no-access`} replace />
}

/**
 * Bridges AuthKit's getAccessToken into api-client at the top level,
 * so all routes (not just /auth/*) can make authenticated API calls.
 */
function AuthBridge() {
  const { getAccessToken } = useAuth()

  // Set synchronously during render — must be available before
  // child components' useQuery calls fire.
  setAccessTokenGetter(getAccessToken)
  return null
}

/**
 * Root-level auth gate shown at "/".
 * If the user has an existing session (tokens or WorkOS cookie), resolves
 * their tenant and redirects straight to the correct dashboard — avoiding
 * a flash of the login page. Falls back to login if no session exists.
 */
const AUTHKIT_TIMEOUT_MS = 3000

function AuthGate() {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()
  // 'idle' = waiting for AuthKit, 'resolving' = getMe in-flight, 'no_session' = go to login
  const [status, setStatus] = useState<'idle' | 'resolving' | 'no_session'>('idle')
  const [authKitTimedOut, setAuthKitTimedOut] = useState(false)

  // Same timeout as ProtectedRoute — stop waiting for AuthKit after 3s
  useEffect(() => {
    if (!isLoading) {
      setAuthKitTimedOut(false)
      return
    }
    const timer = setTimeout(() => setAuthKitTimedOut(true), AUTHKIT_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [isLoading])

  useEffect(() => {
    if ((isLoading && !authKitTimedOut) || status !== 'idle') return

    const hasStoredAuth = localStorage.getItem('access_token') || localStorage.getItem('refresh_token')

    if (!user && !hasStoredAuth) {
      setStatus('no_session')
      return
    }

    setStatus('resolving')
    getMe()
      .then((data) => {
        if (data.tenant?.slug) {
          const perms: string[] = data.permissions ?? []
          const hasDashboard = perms.includes('dashboard.view')
          const hasSearch = perms.includes('products.search')

          if (hasDashboard) navigate(`/${data.tenant.slug}/dashboard`, { replace: true })
          else if (hasSearch) navigate(`/${data.tenant.slug}/engineer`, { replace: true })
          else navigate(`/${data.tenant.slug}/no-access`, { replace: true })
        } else {
          console.warn('[auth:routes] AuthGate no_tenant — capturing session_id before redirect')
          debugDumpAuthState('AuthGate no_tenant before clear')
          captureSessionId()
          sessionStorage.setItem('logout_reason', 'no_tenant')
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          debugDumpAuthState('AuthGate no_tenant after clear')
          navigate('/auth/error', { replace: true })
        }
      })
      .catch((err) => {
        if (isAccountBlocked(err)) {
          console.warn('[auth:routes] AuthGate account_blocked — capturing session_id before redirect')
          debugDumpAuthState('AuthGate account_blocked before clear')
          captureSessionId()
          sessionStorage.setItem('logout_reason', 'account_blocked')
          sessionStorage.setItem('logout_message', getBlockedMessage(err))
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          debugDumpAuthState('AuthGate account_blocked after clear')
          navigate('/auth/error', { replace: true })
          return
        }
        console.warn('[auth:routes] AuthGate getMe error (not blocked)', err)
        navigate('/auth/login', { replace: true })
      })
  }, [user, isLoading, authKitTimedOut, navigate, status])

  if (status === 'no_session') {
    return <Navigate to="/auth/login" replace />
  }

  // Show spinner while AuthKit loads or getMe() resolves
  return <LoadingSpinner message="Signing you in..." />
}

export function AppRoutes() {
  return (
    <AuthKitProvider
      clientId={WORKOS_CLIENT_ID}
      redirectUri={window.location.origin + '/auth/callback'}
      onRedirectCallback={(params) => {
        // Capture tokens from AuthKit's code exchange before CallbackPage renders
        if (params.accessToken) {
          localStorage.setItem('access_token', params.accessToken)
        }
        if (params.refreshToken) {
          localStorage.setItem('refresh_token', params.refreshToken)
        }
      }}
    >
      <AuthBridge />
      <SessionExpiryProvider>
      <AccountBlockedProvider>
      <Routes>
        {/* Root - check session before showing login */}
        <Route path="/" element={<AuthGate />} />

        {/* Auth Routes */}
        <Route path="/auth/*" element={<AuthApp />} />

        {/* Featurebase portal SSO — Featurebase sends signed-out portal
            visitors here; ProtectedRoute signs them in to us first. Sits
            before the /:tenantSlug routes so "featurebase" isn't read as a
            tenant slug. */}
        <Route path={FEATUREBASE_SSO_PATH} element={
          <ProtectedRoute>
            <FeaturebaseSsoPage />
          </ProtectedRoute>
        } />

        {/* Legacy /onboarding/* redirects */}
        <Route path="/onboarding/*" element={<Navigate to="/" replace />} />

        {/* Engineer side - mobile */}
        {/* EngineerLayoutGuard checks search_products — redirects to dashboard/no-access if missing */}
        <Route path="/:tenantSlug/engineer/*" element={
          <ProtectedRoute>
            <EngineerLayoutGuard />
          </ProtectedRoute>
        } />

        {/* No-access page — outside admin layout so it doesn't show the sidebar */}
        <Route path="/:tenantSlug/no-access" element={
          <ProtectedRoute>
            <NoAccessPage />
          </ProtectedRoute>
        } />

        {/* Admin side - desktop with sidebar (tenant-slug URLs) */}
        {/* AdminLayoutGuard allows entry for any admin-side permission
         * (view_dashboard / view_settings / view_all_orders /
         * view_approvals / manage_users). Individual child routes guard
         * themselves via RequirePermission. */}
        <Route
          path="/:tenantSlug/*"
          element={
            <ProtectedRoute>
              <AdminLayoutGuard />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={
            <RequirePermission permission="view_dashboard">
              <OnboardingDashboard />
            </RequirePermission>
          } />
          {/* <Route path="users" element={<OnboardingUsers />} /> */}
          <Route path="orders" element={<Orders />} />
          <Route path="orders/:orderId" element={<OrderDetail />} />
          <Route path="orders/:orderId/supplier-changes" element={<SupplierChangeOrder />} />
          <Route path="profile" element={<OnboardingProfile />} />
          <Route path="settings" element={
            <RequirePermission permission="view_settings">
              <OnboardingSettings />
            </RequirePermission>
          } />
          <Route path="settings/spend-caps" element={
            <RequirePermission permission="view_settings">
              <SpendCapsSettings />
            </RequirePermission>
          } />
          <Route index element={<AdminIndexRedirect />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Catch-all for completely unknown paths */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </AccountBlockedProvider>
      </SessionExpiryProvider>
    </AuthKitProvider>
  )
}
